import React, { useState, useMemo, useCallback } from "react";
import PageLoader from "components/ui/PageLoader";
import "assets/css/style.css";
import { useGetSubscriptionsQuery } from "apis/subscription";
import { useGetUserQuery } from "apis/user";
import { formatDate } from "utils/helperFunctions";
import Table from "components/ui/Table";

const BeleefSubscriptions = () => {
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const { data, isPending } = useGetSubscriptionsQuery({
    subscriptionType: "beleef",
  });

  const { data: usersData, isPending: usersLoading } = useGetUserQuery();

  const getCoAgents = (userId) => {
    if (!usersData || !userId) return [];
    return usersData.filter((user) => user.mainAgentId === userId);
  };

  const handleExport = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = [
      "#",
      "User Name",
      "Product Name",
      "Co Agents",
      "Status",
      "Amount",
      "Period Start",
      "Period End",
      "Used Campaigns",
      "Used Price Process",
    ];

    const csvRows = data.map((row, index) => {
      const coAgents = getCoAgents(row.userId?._id);
      const coAgentNames = coAgents.map((ca) => ca.name || "Unknown").join("; ");

      return [
        index + 1,
        row.userId?.name || "N/A",
        row.productId?.name || "N/A",
        coAgentNames || "No co-agents",
        row.status || "",
        row.amount ? (row.amount / 100).toFixed(2) : "0.00",
        row.currentPeriodStart ? formatDate(row.currentPeriodStart) : "N/A",
        row.currentPeriodEnd ? formatDate(row.currentPeriodEnd) : "N/A",
        row.usedCampaigns || 0,
        row.usedPriceProcess || 0,
      ];
    });

    downloadCsv(headers, csvRows, "beleef-subscriptions");
  }, [data, usersData]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "userId.name",
        header: "User Name",
        cell: (row) => <span>{row.userId?.name || "N/A"}</span>,
        isFilter: true,
        minWidth: "150px",
      },
      {
        accessorKey: "productId.name",
        header: "Product Name",
        cell: (row) => <span>{row.productId?.name || "N/A"}</span>,
        isFilter: true,
        minWidth: "200px",
      },
      {
        accessorKey: "coAgents",
        header: "Co Agents",
        cell: (row) => {
          const coAgents = getCoAgents(row.userId?._id);
          if (coAgents.length === 0) {
            return <span className="text-darkgray text-sm">No co-agents</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {coAgents.map((coAgent, index) => (
                <span
                  key={coAgent._id || index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                  title={coAgent.email || coAgent.name}
                >
                  {coAgent.name || "Unknown"}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status;
          let statusClass = "";
          switch (status) {
            case "active": statusClass = "bg-green-100 text-green-800"; break;
            case "trialing": statusClass = "bg-yellow-100 text-yellow-800"; break;
            case "canceled": statusClass = "bg-red-100 text-red-800"; break;
            case "past_due": statusClass = "bg-orange-100 text-orange-800"; break;
            case "unpaid": statusClass = "bg-gray-100 text-gray-800"; break;
            case "expired": statusClass = "bg-red-100 text-red-800"; break;
            default: statusClass = "bg-gray-100 text-gray-800";
          }
          return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusClass}`}>
              {status}
            </span>
          );
        },
        isFilter: true,
      },
      {
        accessorKey: "amount",
        header: "Amount",
        cell: (row) => (
          <span className="font-medium">
            ${row.amount ? (row.amount / 100).toFixed(2) : "0.00"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "currentPeriodStart",
        header: "Period Start",
        cell: (row) => (
          <span>{row.currentPeriodStart ? formatDate(row.currentPeriodStart) : "N/A"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "currentPeriodEnd",
        header: "Period End",
        cell: (row) => (
          <span>{row.currentPeriodEnd ? formatDate(row.currentPeriodEnd) : "N/A"}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "usedCampaigns",
        header: "Used Campaigns",
        cell: (row) => <span>{row.usedCampaigns || 0}</span>,
        isFilter: false,
      },
      {
        accessorKey: "usedPriceProcess",
        header: "Used Price Process",
        cell: (row) => <span>{row.usedPriceProcess || 0}</span>,
        isFilter: false,
      },
    ],
    [usersData]
  );

  if (isPending || usersLoading) {
    return <PageLoader />;
  }

  return (
    <div className="container mx-auto">
      <Table
        title=""
        columns={columns}
        data={data || []}
        pageSize={pageSize}
        ButtonCmp={() => (
          <button onClick={handleExport} className="black-button">
            Export
          </button>
        )}
      />
    </div>
  );
};

// ─── Shared CSV helper ──────────────────────────────────────────────────────

function escapeCsvValue(value) {
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(headers, rows, filename) {
  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default BeleefSubscriptions;